import { ChevronDown, ExternalLink, Package } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { purchaseRequestPath } from "@/routes";
import type { PurchaseRequestPreview } from "@/types";
import { formatDateAR } from "@/utils/date";

interface PurchaseRequestPreviewCardProps {
  preview: PurchaseRequestPreview;
  defaultExpanded?: boolean;
}

export default function PurchaseRequestPreviewCard({ preview, defaultExpanded = true }: PurchaseRequestPreviewCardProps) {
  // Determinar qué items mostrar y si mostrar el alert de fallback
  const showFallback = preview.selected_items.length === 0 && preview.request_items && preview.request_items.length > 0;
  const itemsToShow = showFallback ? preview.request_items : preview.selected_items;

  // Si hay fallback, forzar que el accordion esté abierto por defecto
  const [isTableExpanded, setIsTableExpanded] = useState(defaultExpanded || showFallback);

  return (
    <div className="space-y-4">
      {/* Header de la solicitud */}
      <div className="rounded-md border bg-card p-4 space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-muted-foreground">Solicitud</div>
            <a
              href={purchaseRequestPath(preview.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-semibold text-primary hover:underline inline-flex items-center gap-1.5"
            >
              #{preview.request_number}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <Badge variant="outline" className="capitalize text-xs">
            {preview.status}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div>
            <div className="text-xs text-muted-foreground">Obra</div>
            <div className="text-sm font-medium">{preview.sector_name}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Fecha</div>
            <div className="text-sm font-medium">{formatDateAR(preview.created_at)}</div>
          </div>
        </div>
        {preview.supplier_names.length > 0 && (
          <div className="pt-2">
            <div className="text-xs text-muted-foreground mb-1">Proveedores</div>
            <div className="flex flex-wrap gap-1">
              {preview.supplier_names.map((name, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabla de items con toggle estilo accordion */}
      <div className="space-y-2">
        <button
          onClick={() => setIsTableExpanded(!isTableExpanded)}
          className="flex flex-1 w-full items-center justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-ring/50 focus-visible:border-ring focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50"
        >
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span>{showFallback ? "Items del Requerimiento" : "Productos Seleccionados"}</span>
          </div>
          <ChevronDown
            className={`text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200 ${
              isTableExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        {isTableExpanded && (
          <div className="space-y-3">
            {/* Alert informativo cuando no hay productos seleccionados */}
            {showFallback && (
              <Alert>
                <AlertDescription>
                  Esta solicitud de compra no tiene orden de compra asociada. Estás viendo los items del requerimiento original.
                </AlertDescription>
              </Alert>
            )}

            <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itemsToShow.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                      {showFallback ? "Sin items en el requerimiento" : "Sin productos seleccionados"}
                    </TableCell>
                  </TableRow>
                ) : (
                  itemsToShow.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium whitespace-normal break-words">
                        {item.description}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-normal break-words">
                        {item.supplier_name ?? "—"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">{item.quantity} {item.unit}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
